/**
 * Payroll / approval API. Uses POST /api/time-entries/payroll-ledger/approve-hours and POST /api/time-entries/payroll-ledger.
 * Payroll ledger returns aggregated rows (approvalDate, workDate, totalHours, amount), not raw time entries.
 */

import { createAuthenticatedApi } from "@/lib/utils";
import { toCivilYmd } from "@/lib/utils/date/date-only";
import { getActiveRoleRequestHeaders } from "@/lib/utils/api/active-role-header";
import { resolveOpenApiBasePath } from "@/lib/utils/api/openapi-configuration";
import { resolveEffectiveTenantNumber } from "@/lib/utils/env/effective-tenant";
import { runStaffReviewAction } from "@/lib/review-concurrency/run-staff-review-action";
import { TimeClockApi } from "@/openapi/generated";
import type {
  GetPayrollLedgerEntryRequest,
  GetPayrollLedgerEntryResponse,
  PaginatedResultOfGetPayrollLedgerEntryResponse,
} from "@/openapi/generated/models";

const TENANT_ID = resolveEffectiveTenantNumber();
export const PAYROLL_LEDGER_SCROLL_PAGE_SIZE = 500;

/** One row from POST /api/time-entries/payroll-ledger (actual API response shape). */
export interface PayrollLedgerEntry {
  id: string;
  approvalDate: string;
  approvedBy: string;
  userId: number;
  workDate: string;
  totalHours: number;
  hourlyRate: number;
  amount: number;
  createdAt: string;
  timeEntryType: { id: number; name: string };
  updatedAt: string | null;
  entryType?: string;
}

export interface ApproveTimeEntriesPayload {
  entryIds: string[];
  note?: string | null;
}

function toDateString(value: Date | string): string {
  if (value instanceof Date) {
    return toCivilYmd(value) ?? value.toISOString().slice(0, 10);
  }
  return String(value).slice(0, 10);
}

function toIsoString(value: Date | string | null | undefined): string | null {
  if (value == null) return null;
  if (value instanceof Date) return value.toISOString();
  return String(value);
}

function parsePayrollMoney(value: unknown): number {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : 0;
  }
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
}

export function mapPayrollLedgerResponse(
  res: GetPayrollLedgerEntryResponse,
): PayrollLedgerEntry {
  return {
    id: res.id,
    approvalDate: toDateString(res.approvalDate),
    approvedBy: res.approvedBy ?? "",
    userId: Number(res.userId),
    workDate: toDateString(res.workDate),
    totalHours: Number(res.totalHours),
    hourlyRate: parsePayrollMoney(res.hourlyRate),
    amount: parsePayrollMoney(res.amount),
    createdAt: toIsoString(res.createdAt) ?? "",
    updatedAt: toIsoString(res.updatedAt),
    timeEntryType: {
      id: Number(res.timeEntryType?.id ?? 0),
      name: res.timeEntryType?.name ?? "-",
    },
    entryType: res.timeEntryType?.name ?? undefined,
  };
}

export interface ScrollPayrollLedgerParams {
  page: number;
  pageSize?: number;
  lastSelectedId?: string;
  request: GetPayrollLedgerEntryRequest;
}

export async function scrollPayrollLedger(
  accessToken: string,
  params: ScrollPayrollLedgerParams,
  tenantId: number = TENANT_ID,
): Promise<PaginatedResultOfGetPayrollLedgerEntryResponse> {
  const api = createAuthenticatedApi(TimeClockApi, accessToken);
  return api.apiTimeEntriesPayrollLedgerPost({
    getPayrollLedgerEntryRequest: params.request,
    page: params.page,
    pageSize: params.pageSize ?? PAYROLL_LEDGER_SCROLL_PAGE_SIZE,
    lastSelectedId: params.lastSelectedId,
    xTenantID: tenantId,
  });
}

function todayLocalDate(): Date {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

/**
 * Approve selected time entries; they move to payroll ledger.
 * POST /api/time-entries/payroll-ledger/approve-hours
 * approvalDate is a Date (OpenAPI); runtime JSON is yyyy-mm-dd via generated ToJSON.
 */
export async function approveTimeEntries(
  accessToken: string,
  payload: ApproveTimeEntriesPayload,
): Promise<void> {
  await runStaffReviewAction(async () => {
  const api = createAuthenticatedApi(TimeClockApi, accessToken);
  await api.apiTimeEntriesPayrollLedgerApproveHoursPost({
    approveTimeEntriesRequest: {
      ids: payload.entryIds,
      approvalDate: todayLocalDate(),
      note: payload.note ?? null,
    },
    xTenantID: TENANT_ID,
  });
  });
}

/**
 * Fetch payroll ledger (approved hours) with optional filters.
 * POST /api/time-entries/payroll-ledger
 * Returns actual API shape: approvalDate, workDate, totalHours, amount, etc.
 */
export async function getPayrollLedger(
  accessToken: string,
  request: {
    userId?: number | null;
    entryTypeId?: number | null;
    fromDate?: string | null;
    toDate?: string | null;
  } = {},
): Promise<PayrollLedgerEntry[]> {
  const query = new URLSearchParams();
  query.set("page", "1");
  query.set("pageSize", "500");
  const url = `${resolveOpenApiBasePath()}/api/time-entries/payroll-ledger?${query.toString()}`;
  const body = {
    userId: request.userId ?? undefined,
    entryTypeId: request.entryTypeId ?? undefined,
    fromDate: request.fromDate ?? undefined,
    toDate: request.toDate ?? undefined,
  };
  const res = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...getActiveRoleRequestHeaders(),
      Authorization: `Bearer ${accessToken}`,
      "X-Tenant-ID": String(TENANT_ID),
    },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `Payroll ledger ${res.status}`);
  }
  const data = (await res.json()) as {
    items?: GetPayrollLedgerEntryResponse[];
    totalCount?: number;
  };
  const items = (data.items ?? []).map((item) =>
    mapPayrollLedgerResponse(item),
  );
  items.sort((a, b) => {
    const d = (b.approvalDate || "").localeCompare(a.approvalDate || "");
    if (d !== 0) return d;
    return (b.workDate || "").localeCompare(a.workDate || "");
  });
  return items;
}

/**
 * Get approved payroll ledger for a single user (e.g. teacher Payroll Hours view).
 */
export async function getApprovedTimeEntries(
  accessToken: string,
  userId: number,
): Promise<PayrollLedgerEntry[]> {
  return getPayrollLedger(accessToken, { userId });
}
